import { Dayjs } from 'dayjs';
import { MembershipCategory } from '../model/members/MembershipCategory';
import { BillingSettings } from '../model/client/ClientSettings';
import { calculateProRata, nextCycleEnd } from './billingCycle';

// Membership end-date helpers. The monthly branches defer to the shared
// billing-cycle util (billingCycle.tsx, the frontend mirror of the backend
// BillingCycleService) so the date lands on the client's configured cycle day
// rather than the calendar month-end. When no billing config is supplied the
// cycle util falls back to "last day of month", preserving legacy behaviour.

// Fixed-period frequencies advance valid_to by a whole number of months rather
// than onto the monthly billing cycle — mirrors the backend rollover switches
// (MemberService / PaymentService). Monthly (and OneTime) are not listed and
// fall through to the pro-rata / cycle path. Keys are lower-cased to match the
// serialized PaymentFrequency names (Quarterly / SixMonths / Annual).
const FIXED_PERIOD_MONTHS: Record<string, number> = {
  quarterly: 3,
  sixmonths: 6,
  annual: 12,
};

/**
 * End date for a NEW membership starting on `startDate` (joining-fee flow).
 * For monthly: the pro-rated first-period end for that join date — i.e. the
 * exact valid_to that matches the pro-rata amount we charge (cycle anchor, or
 * the following anchor when the join date is on/after the pro-rata cut-off).
 * For a fixed period (quarterly / six-month / annual): that many months from
 * the start date, minus a day (valid_to is inclusive).
 */
export const calculateEndDate = (
  startDate: Dayjs,
  // Nullable: a quick-create may not have a plan chosen yet (the server then
  // assigns the hidden "Default New Member" placeholder). No plan means no fixed
  // period, so it takes the same pro-rata path Monthly and OneTime already do.
  category: MembershipCategory | null,
  billing?: BillingSettings
): Dayjs => {
  const months = category ? FIXED_PERIOD_MONTHS[category.paymentFrequency.toLowerCase()] : undefined;
  if (months != null) {
    return startDate.add(months, 'month').subtract(1, 'day');
  }
  // Monthly (and any other frequency, defensively): pro-rata period end.
  return calculateProRata(0, startDate, billing).validTo;
};

/**
 * validTo for a subscription payment covering the picked membership `month`.
 * For monthly: the end of the billing cycle that STARTS in the selected month
 * — e.g. with cycle day 6 (months run 7th→6th) picking June yields 6 Jul. For
 * the legacy month-end config this is the last day of the selected month.
 * For a fixed period (quarterly / six-month / annual): end of the month that
 * many months on.
 */
export const calculateValidToDate = (
  month: Dayjs,
  category: MembershipCategory | null,
  billing?: BillingSettings
): Dayjs => {
  if (!category) {
    return month.endOf('month');
  }

  const months = FIXED_PERIOD_MONTHS[category.paymentFrequency.toLowerCase()];
  if (months != null) {
    return month.add(months, 'month').endOf('month');
  }

  // Monthly: the cycle that starts in the selected month ends on the configured
  // cycle day of the following month. For the legacy month-end config (no fixed
  // cycle day) that cycle is the calendar month itself, so keep month-end.
  if (billing?.monthlyCycleDay == null) {
    return month.endOf('month');
  }
  return nextCycleEnd(month, billing);
};
